import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import usePageMetadata from '../utils/usePageMetadata';
import './Home.css';

const Home = () => {
  const { token } = useAuth();
  const navigate = useNavigate();

  usePageMetadata({
    title: 'The Ad Shop | Unified Analytics for Google Analytics, AdSense & Search Console',
    description:
      'The Ad Shop is the unified analytics platform built for publishers to combine Google Analytics 4, AdSense, and Google Search Console data, generate actionable insights, and automate revenue optimization.',
    canonicalUrl: 'https://theadshop.co/',
    meta: [
      { property: 'og:title', content: 'The Ad Shop | Unified Analytics for Google Analytics, AdSense & Search Console' },
      {
        property: 'og:description',
        content:
          'Bring GA4, AdSense, and Google Search Console metrics together in a single dashboard with automated insights, compliance monitoring, and revenue optimization.',
      },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: 'https://theadshop.co/' },
      { property: 'og:image', content: 'https://theadshop.co/images/dashboard-preview.webp' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'The Ad Shop | Unified Analytics for Google Analytics, AdSense & Search Console' },
      {
        name: 'twitter:description',
        content:
          'Understand traffic and revenue in seconds with the unified analytics dashboard built for publishers.',
      },
      { name: 'twitter:image', content: 'https://theadshop.co/images/dashboard-preview.webp' },
    ],
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'The Ad Shop',
        url: 'https://theadshop.co/',
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://theadshop.co/?q={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
        inLanguage: 'en-US',
        description:
          'Unified analytics platform for publishers combining Google Analytics 4, AdSense, and Google Search Console data with automated insights.',
        publisher: {
          '@type': 'Organization',
          name: 'The Media Shop, LLC',
          url: 'https://theadshop.co/',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'The Media Shop, LLC',
        url: 'https://theadshop.co/',
        logo: 'https://theadshop.co/images/the-ad-shop-logo.svg',
        sameAs: [
          'https://www.linkedin.com/company/the-media-shop-llc/',
          'https://twitter.com/theadshop',
        ],
        contactPoint: {
          '@type': 'ContactPoint',
          email: 'support@theadshop.co',
          contactType: 'customer support',
          areaServed: 'US',
          availableLanguage: ['English'],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'The Ad Shop Analytics Dashboard',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        offers: {
          '@type': 'Offer',
          price: '0.00',
          priceCurrency: 'USD',
          availability: 'https://schema.org/InStock',
          url: 'https://theadshop.co/signup',
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '5',
          reviewCount: '12',
          bestRating: '5',
          worstRating: '1',
        },
        description:
          'Analytics dashboard that unifies Google Analytics 4, AdSense, and Google Search Console data for publishers with actionable insights.',
        featureList: [
          'Unified GA4, AdSense, and Search Console data',
          'Real-time metrics and insights',
          'Automated compliance monitoring',
          'Content performance analytics',
          'Advanced filtering and segmentation',
        ],
        applicationSubCategory: 'Analytics Software',
        screenshot: 'https://theadshop.co/images/dashboard-preview.webp',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'What insights does The Ad Shop provide?',
            acceptedAnswer: {
              '@type': 'Answer',
              text:
                'The Ad Shop unifies GA4, AdSense, and Google Search Console metrics to surface actionable insights on traffic, engagement, revenue, and search performance, helping publishers pinpoint optimization opportunities.',
            },
          },
          {
            '@type': 'Question',
            name: 'How long does it take to connect The Ad Shop to my Google accounts?',
            acceptedAnswer: {
              '@type': 'Answer',
              text:
                'You can connect Google Analytics 4, AdSense, and Google Search Console in minutes through our secure OAuth workflow, with automated data syncing once connected.',
            },
          },
          {
            '@type': 'Question',
            name: 'Does The Ad Shop help with compliance and consent management?',
            acceptedAnswer: {
              '@type': 'Answer',
              text:
                'Yes. The Ad Shop monitors CMP status, privacy requirements, and consent signals so you can stay compliant across regions and platforms.',
            },
          },
        ],
      },
    ],
  });

  const handleGetStarted = () => {
    if (token) {
      navigate('/dashboard');
    } else {
      navigate('/signup');
    }
  };

  return (
    <div className="home">
      {/* Hero Section */}
      <div className="hero-section">
        <div className="hero-container">
          <div className="hero-content">
            <h1 className="hero-title">
              <span className="highlight">Analytics with Answers</span>
            </h1>
            <p className="hero-subtitle">
              The all-in-one growth platform that turns your Google Analytics, AdSense, and Search Console data into clarity. Instantly filter your traffic, revenue, and content performance, understand exactly what's working, and get page-level insights you can act on today.
            </p>
            <div className="hero-cta">
              <button onClick={handleGetStarted} className="btn-primary">
                Sign Up
              </button>
              <p className="cta-subtext">Free to get started • No credit card required</p>
              <p className="demo-promo">Want to see our dashboards without signing up?</p>
              <a href="/demo" className="btn-demo">
                Try Our Demo
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Preview Section */}
      <div className="preview-section">
        <div className="preview-container">
          <div className="preview-content">
            <h2 className="section-title">See Your Data Like Never Before</h2>
            <p className="section-description preview-description">
              Four powerful dashboards designed to help you understand your traffic, optimize your content, 
              maximize revenue, and stay compliant. Watch how it all works together.
            </p>
            <div className="dashboard-preview">
              <div className="youtube-embed-container">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/wz9ule5myRQ"
                  title="The Ad Shop Analytics Dashboard Preview"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="youtube-embed"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Three Dashboards Section */}
      <div className="features-section">
        <div className="features-container">
          <div className="section-header">
            <h2 className="section-title">Four Dashboards, One Platform</h2>
            <p className="section-description">
              Everything you need to grow your site, all in one place
            </p>
          </div>

          <div className="dashboards-grid">
            {/* Analytics Dashboard */}
            <div className="dashboard-card">
              <div className="dashboard-card-header">
                <div className="dashboard-icon">📊</div>
                <h3>Analytics Dashboard</h3>
              </div>
              <p className="dashboard-description">
                Unify Google Analytics 4, AdSense, and Google Search Console data in one powerful view. 
                Track performance, revenue, and search metrics with real-time insights.
              </p>
              <ul className="dashboard-features">
                <li>📈 Real-time traffic metrics (Users, Sessions, Pageviews)</li>
                <li>💰 AdSense revenue tracking and RPM analysis</li>
                <li>🔍 Search Console performance data</li>
                <li>📊 Interactive charts and trend visualizations</li>
                <li>🎯 Automated insights and recommendations</li>
                <li>🔧 Advanced filtering by device, geography, and traffic source</li>
              </ul>
            </div>

            {/* Content Dashboard */}
            <div className="dashboard-card">
              <div className="dashboard-card-header">
                <div className="dashboard-icon">📝</div>
                <h3>Content Dashboard</h3>
              </div>
              <p className="dashboard-description">
                Understand which content performs best and optimize your publishing strategy. Track page performance, 
                social media engagement, and content insights.
              </p>
              <ul className="dashboard-features">
                <li>📄 Page-level performance analytics</li>
                <li>📰 RSS feed integration and content discovery</li>
                <li>📱 Social media platform tracking (Facebook, Twitter, Instagram, etc.)</li>
                <li>🎯 Content insights and optimization recommendations</li>
                <li>📊 Category and section performance breakdowns</li>
                <li>🔥 Trending content identification</li>
              </ul>
            </div>

            {/* Revenue Dashboard */}
            <div className="dashboard-card">
              <div className="dashboard-card-header">
                <div className="dashboard-icon">💰</div>
                <h3>Revenue Dashboard</h3>
              </div>
              <p className="dashboard-description">
                Maximize your ad revenue with detailed insights into ad performance, partner performance, and revenue optimization. 
                Track RPM, CPM, viewability, and identify top-performing ad units and partners.
              </p>
              <ul className="dashboard-features">
                <li>💰 Revenue tracking and RPM analysis</li>
                <li>📊 Ad unit performance breakdown</li>
                <li>🤝 Ad partner performance comparison</li>
                <li>👁️ Viewability metrics and optimization</li>
                <li>🌍 Geographic revenue analysis</li>
                <li>📈 Revenue trends and forecasting</li>
              </ul>
            </div>

            {/* Compliance Dashboard */}
            <div className="dashboard-card">
              <div className="dashboard-card-header">
                <div className="dashboard-icon">✅</div>
                <h3>Compliance Dashboard</h3>
              </div>
              <p className="dashboard-description">
                Stay ahead of ad policy and privacy requirements. Monitor compliance checks, fix issues proactively, 
                and ensure your site meets all standards.
              </p>
              <ul className="dashboard-features">
                <li>📋 Automated compliance scanning</li>
                <li>📄 ads.txt validation and generation</li>
                <li>🤖 robots.txt monitoring</li>
                <li>🔒 Privacy policy compliance checks</li>
                <li>🍪 Consent Management Platform (CMP) monitoring</li>
                <li>📊 Compliance score and detailed reporting</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="cta-section">
        <div className="cta-container">
          <h2 className="cta-title">Ready to Transform Your Analytics?</h2>
          <p className="cta-description">
            Sign up today and start experiencing unified GA4, AdSense, and Google Search Console analytics.
          </p>
          <button onClick={handleGetStarted} className="btn-cta">
            Sign Up Now
          </button>
          <p className="cta-note">Free forever • No credit card required • Setup in minutes</p>
        </div>
      </div>
    </div>
  );
};

export default Home;
